import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chatService } from '@services/chatService';
import {
  COMPONENT_PERMISSION_KEYS,
  COMPONENT_TYPE_ORDER,
  type ChatComponentItem,
  type ComponentType,
} from '../types/chat-component';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

export const COMPONENT_TAB_META: Record<
  ComponentType,
  { key: ComponentType; label: string }
> = {
  order: { key: 'order', label: 'Đơn hàng' },
  product: { key: 'product', label: 'Sản phẩm' },
  ingredient: { key: 'ingredient', label: 'Nguyên liệu' },
  customer: { key: 'customer', label: 'Khách hàng' },
  supplier: { key: 'supplier', label: 'Nhà cung cấp' },
  employee: { key: 'employee', label: 'Nhân viên' },
};

type TabCache = {
  items: ChatComponentItem[];
  page: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  keyword: string;
};

const emptyTabCache = (): TabCache => ({
  items: [],
  page: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,
  keyword: '',
});

export const getAllowedComponentTypes = (
  permissions?: Record<string, boolean> | null,
  isOwner?: boolean,
): ComponentType[] => {
  if (isOwner) return [...COMPONENT_TYPE_ORDER];
  return COMPONENT_TYPE_ORDER.filter((type) => Boolean(permissions?.[COMPONENT_PERMISSION_KEYS[type]]));
};

interface UseComponentPickerArgs {
  open: boolean;
  permissions?: Record<string, boolean> | null;
  isOwner?: boolean;
}

export const useComponentPicker = ({
  open,
  permissions,
  isOwner = false,
}: UseComponentPickerArgs) => {
  const allowedTypes = useMemo(
    () => getAllowedComponentTypes(permissions, isOwner),
    [permissions, isOwner],
  );

  const [selectedType, setSelectedType] = useState<ComponentType>('order');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [cache, setCache] = useState<Partial<Record<ComponentType, TabCache>>>({});
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const prefetchIdRef = useRef(0);
  const listRequestIdRef = useRef(0);

  useEffect(() => {
    if (!allowedTypes.length) return;
    if (!allowedTypes.includes(selectedType)) {
      setSelectedType(allowedTypes[0]);
    }
  }, [allowedTypes, selectedType]);

  const updateTabCache = useCallback((type: ComponentType, patch: Partial<TabCache>) => {
    setCache((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || emptyTabCache()),
        ...patch,
      },
    }));
  }, []);

  const fetchTab = useCallback(
    async (type: ComponentType, options?: { page?: number; keyword?: string; append?: boolean }) => {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        updateTabCache(type, {
          items: [],
          page: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: 'Thiếu access token',
          keyword: options?.keyword || '',
        });
        return;
      }

      const page = options?.page ?? 1;
      const keyword = options?.keyword ?? '';
      const append = Boolean(options?.append);
      const requestId = ++listRequestIdRef.current;

      updateTabCache(type, {
        loading: !append,
        loadingMore: append,
        error: null,
        keyword,
      });

      try {
        const result = await chatService.listChatComponents(token, type, {
          page,
          pageSize: PAGE_SIZE,
          keyword,
        });

        if (requestId !== listRequestIdRef.current && append) {
          // ignore stale append only when superseded; prefetch uses own ids via Promise.allSettled path
        }

        setCache((prev) => {
          const current = prev[type] || emptyTabCache();
          return {
            ...prev,
            [type]: {
              items: append ? [...current.items, ...result.items] : result.items,
              page: result.page,
              hasMore: result.hasMore,
              loading: false,
              loadingMore: false,
              error: null,
              keyword,
            },
          };
        });
      } catch {
        updateTabCache(type, {
          loading: false,
          loadingMore: false,
          error: 'Không tải được danh sách',
          ...(append ? {} : { items: [], page: 0, hasMore: false }),
          keyword,
        });
      }
    },
    [updateTabCache],
  );

  // Prefetch all allowed tabs when opening picker
  useEffect(() => {
    if (!open) {
      setCache({});
      setSearchInput('');
      setSearchKeyword('');
      return;
    }

    if (!allowedTypes.length) return;

    const prefetchId = ++prefetchIdRef.current;
    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    allowedTypes.forEach((type) => {
      updateTabCache(type, {
        ...emptyTabCache(),
        loading: true,
        keyword: '',
      });
    });

    void Promise.allSettled(
      allowedTypes.map(async (type) => {
        try {
          const result = await chatService.listChatComponents(token, type, {
            page: 1,
            pageSize: PAGE_SIZE,
            keyword: '',
          });
          if (prefetchId !== prefetchIdRef.current) return;
          updateTabCache(type, {
            items: result.items,
            page: result.page,
            hasMore: result.hasMore,
            loading: false,
            loadingMore: false,
            error: null,
            keyword: '',
          });
        } catch {
          if (prefetchId !== prefetchIdRef.current) return;
          updateTabCache(type, {
            items: [],
            page: 0,
            hasMore: false,
            loading: false,
            loadingMore: false,
            error: 'Không tải được danh sách',
            keyword: '',
          });
        }
      }),
    );
  }, [open, allowedTypes, updateTabCache]);

  // Debounce search input → keyword for active tab
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setSearchKeyword(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, open]);

  // Search on active tab only (empty keyword = prefetch / cached page1)
  useEffect(() => {
    if (!open) return;
    const tab = cacheRef.current[selectedType];

    if (searchKeyword === '') {
      // Prefetch owns empty keyword; avoid racing another page=1 fetch
      if (!tab || tab.loading || tab.keyword === '') return;
      // User cleared search after a previous keyword → reload empty
    } else if (tab && tab.keyword === searchKeyword && (tab.page > 0 || tab.loading)) {
      return;
    }

    void fetchTab(selectedType, { page: 1, keyword: searchKeyword, append: false });
  }, [open, selectedType, searchKeyword, fetchTab]);

  const activeCache = cache[selectedType] || emptyTabCache();

  const loadMore = useCallback(() => {
    if (!open) return;
    if (activeCache.loading || activeCache.loadingMore || !activeCache.hasMore) return;
    void fetchTab(selectedType, {
      page: activeCache.page + 1,
      keyword: searchKeyword,
      append: true,
    });
  }, [open, activeCache, selectedType, searchKeyword, fetchTab]);

  const selectType = useCallback((type: ComponentType) => {
    setSelectedType(type);
    setSearchInput('');
    setSearchKeyword('');
  }, []);

  const commitSearch = useCallback(() => {
    setSearchKeyword(searchInput.trim());
  }, [searchInput]);

  return {
    allowedTypes,
    selectedType,
    selectType,
    searchInput,
    setSearchInput,
    commitSearch,
    activeCache,
    loadMore,
    tabMeta: COMPONENT_TAB_META,
  };
};
